import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';
import PhoneInput from 'react-phone-input-2';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomInput,
  CustomPhoneNumber, defaultValidateMessages,
  DiscardModal, EIconName, emailPattern,
  InputReadOnly
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateCompanyContactDetails } from '../../../../../../store/company/company.thunks';

// Styles
import './company-contact-details.scss';

// Models
import { ICompanyProfile } from '../../../../../../interfaces/company.interfaces';

export type TCompanyContactDetailsRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TCompanyContactDetailsProps = {
  company: ICompanyProfile;
};

export const CompanyContactDetails = forwardRef<TCompanyContactDetailsRef, TCompanyContactDetailsProps>((
  { company }: TCompanyContactDetailsProps, ref
) => {
  const [form] = Form.useForm<{
    email: string,
  }>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [disableAction, setDisableAction] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const [phoneValue, setPhoneValue] = useState(company.phone);
  const [phoneCountry, setPhoneCountry] = useState('');
  const [formattedPhone, setFormattedPhone] = useState(company.phone);
  const [isPhoneValueValid, setIsPhoneValueValid] = useState<boolean | null>(true);

  const [landlineValue, setLandlineValue] = useState(company.landline);
  const [landlineCountry, setLandlineCountry] = useState('');
  const [formattedLandline, setFormattedLandline] = useState(company.landline);
  const [isLandlineValueValid, setIsLandlineValueValid] = useState<boolean | null>(true);

  const [faxValue, setFaxValue] = useState(company.fax);
  const [faxCountry, setFaxCountry] = useState('');
  const [formattedFax, setFormattedFax] = useState(company.fax);
  const [isFaxValueValid, setIsFaxValueValid] = useState<boolean | null>(true);

  const dispatch = useDispatch<any>();

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      form.resetFields();
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();

      setPhoneValue(company.phone);
      setFormattedPhone(company.phone);
      setFaxValue(company.fax);
      setFormattedFax(company.fax);
      setLandlineValue(company.landline);
      setFormattedLandline(company.landline);
      return;
    }
    toggleDiscardModal();
  }

  const onValuesChange = (_: { [key: string]: string | string[] }, { email }: {
    email: string,
  }): void => {
    const isHasEmailChanges = company?.company_email !== email;
    const isHasPhoneChanges = company?.phone !== formattedPhone;
    const isHasLandlineChanges = company?.landline !== formattedLandline;
    const isHasFaxChanges = company?.fax !== formattedFax;

    const isHasChanges = isHasEmailChanges || isHasPhoneChanges || isHasLandlineChanges || isHasFaxChanges;

    setDisableAction(email ? !new RegExp(emailPattern).test(email) : false);

    setHasChanges(
      isHasChanges && !!isPhoneValueValid && !!isLandlineValueValid && !!isFaxValueValid
    );
  }

  const saveChanges = async (): Promise<void> => {
    const { email: company_email } = form.getFieldsValue();
    setSavingChanges(true);
    const { payload } = await dispatch(updateCompanyContactDetails({
      company_email,
      fax: formattedFax ?? company.fax,
      landline: formattedLandline ?? company.landline,
      phone: formattedPhone ?? company.phone
    }));
    setSavingChanges(false);
    if (!payload) return;
    setEditMode(false);
    setHasChanges(false);
    form.resetFields();
  }

  useImperativeHandle(ref, () => ({
    isHasChanges(): boolean {
      return hasChanges;
    },
    resetState(): void {
      form.setFieldsValue({
        email: company.company_email ?? '',
      });
      setHasChanges(false);
      setEditMode(false);
      setSavingChanges(false);

      setPhoneValue(company.phone);
      setFormattedPhone(company.phone);
      setFaxValue(company.fax);
      setFormattedFax(company.fax);
      setLandlineValue(company.landline);
      setFormattedLandline(company.landline);
    }
  }));

  useEffect(() => {
    const { email } = form.getFieldsValue();
    const isHasEmailChanges = typeof email !== 'undefined' ? company?.company_email !== email : false;
    const isHasPhoneChanges = company?.phone !== formattedPhone;
    const isHasLandlineChanges = company?.landline !== formattedLandline;
    const isHasFaxChanges = company?.fax !== formattedFax;
    const isHasChanges = isHasEmailChanges || isHasPhoneChanges || isHasLandlineChanges || isHasFaxChanges;

    setDisableAction(email ? !new RegExp(emailPattern).test(email) : false);

    setHasChanges(isHasChanges && !!isPhoneValueValid && !!isLandlineValueValid && !!isFaxValueValid)
  }, [formattedPhone, formattedLandline, formattedFax]);

  return (
    <div className={ `company-contact-details ${ editMode ? 'edit-mode' : '' }` }>
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="company-contact-details--label business--label">
        <div className="label-title">
          Company Contact Details
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ (company?.fax || company.phone || company.landline) ? 'Edit' : 'Add' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { (hasChanges && !disableAction) && <CustomButton type={ 'primary' }
                                                              text={ 'Save' }
                                                              size={ 'small' }
                                                              loading={ savingChanges }
                                                              onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form className="company-contact-details--body business--body-items" form={ form }
            onValuesChange={ onValuesChange }>
        <div className="body-item split-items">
          <div className="body-item--field">
            { (!editMode && company?.company_email) &&
              <InputReadOnly label={ 'Email' } value={ company.company_email } textEllipsis={ true }
                             icon={ <CustomIcon name={ EIconName.Mail }/> }/> }

            { editMode &&
              <Form.Item name={ 'email' } initialValue={ company.company_email }
                         messageVariables={ { fieldLabel: 'email' } } rules={ [
                { max: 60 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(emailPattern).test(getFieldValue('email'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 60) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(defaultValidateMessages.invalidEmailFormat));
                  }
                })] }>
                <CustomInput placeholder={ 'Email' } type={ 'text' }/>
              </Form.Item> }
          </div>
          <div className="body-item--field">
            { (!editMode && company?.landline) &&
              <InputReadOnly label={ 'Landline' } value={ company.landline } textEllipsis={ true }
                             icon={ <CustomIcon name={ EIconName.Call }/> }/> }

            { editMode &&
              // <Form.Item name={ 'landline' } initialValue={ null }>
              <CustomPhoneNumber
                PhoneInput={ PhoneInput }
                phone={ company.landline ?? '' }
                isPhoneNumberValid={ (value) => {
                  setIsLandlineValueValid(value);
                } }
                onChangeFormattedPhone={ (value) => {
                  setFormattedLandline(value);
                } }
              />
              // </Form.Item>
            }
          </div>

          <div className="body-item--field">
            { (!editMode && company?.phone) &&
              <InputReadOnly label={ 'Mobile Number' } value={ company.phone } textEllipsis={ true }
                             icon={ <CustomIcon name={ EIconName.Smartphone }/> }/> }

            { editMode &&
              // <Form.Item name={ 'phone' } initialValue={ null }>
              <CustomPhoneNumber
                PhoneInput={ PhoneInput }
                placeholder={ 'Phone Number' }
                phone={ company.phone ?? '' }
                fieldIconName={ EIconName.Smartphone }
                isPhoneNumberValid={ (value) => {
                  setIsPhoneValueValid(value);
                } }
                onChangeFormattedPhone={ (value) => {
                  setFormattedPhone(value);
                } }
              />
              // </Form.Item>
            }
          </div>
          <div className="body-item--field">
            { (!editMode && company?.fax) &&
              <InputReadOnly label={ 'Fax' } value={ company.fax } textEllipsis={ true }
                             icon={ <CustomIcon name={ EIconName.Fax }/> }/> }

            { editMode &&
              // <Form.Item name={ 'fax' } initialValue={ null }>
              <CustomPhoneNumber
                PhoneInput={ PhoneInput }
                placeholder={ 'Fax' }
                errorMessage={ 'The fax is not correct' }
                phone={ company.fax ?? '' }
                fieldIconName={ EIconName.Fax }
                isPhoneNumberValid={ (value) => {
                  setIsFaxValueValid(value);
                } }
                onChangeFormattedPhone={ (value) => {
                  setFormattedFax(value);
                } }
              />
              // </Form.Item>
            }
          </div>
        </div>
      </Form>
    </div>
  );
})
